import { useState } from 'react';
import { useTranslations } from 'use-intl';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { userFacingErrorMessage } from '@/utils/userFacingError';

const personalDomains = ['gmail.com', 'me.com', 'live.com', 'yahoo.com', 'hotmail.com', 'aol.com', 'hotmail.co.uk', 'hotmail.fr', 'msn.com'];

function isCompanyEmail(email: string) {
  const domain = email.split('@')[1]?.toLowerCase();
  return !!domain && !personalDomains.includes(domain);
}

export default function SignupForm() {
  const t = useTranslations('consultantAuth.signup');
  const { signUp } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSignup = async () => {
    if (!email || !password || !confirmPassword) {
      toast.error(t('errors.fillAll'));
      return;
    }
    if (!isCompanyEmail(email)) {
      toast.error(t('errors.companyEmail'));
      return;
    }
    if (password !== confirmPassword) {
      toast.error(t('errors.mismatch'));
      return;
    }
    if (password.length < 8) {
      toast.error(t('errors.tooShort'));
      return;
    }

    setIsLoading(true);
    try {
      const { error } = await signUp(email, password);
      if (error) throw error;
      toast.success(t('created'));
    } catch (err) {
      const message = userFacingErrorMessage(err, t('errors.failed'));
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('cardTitle')}</CardTitle>
        <CardDescription>{t('cardDescription')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <label htmlFor="signup-email" className="text-sm font-medium">{t('email')}</label>
          <Input id="signup-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t('emailPlaceholder')} />
        </div>
        <div className="space-y-2">
          <label htmlFor="signup-password" className="text-sm font-medium">{t('password')}</label>
          <Input id="signup-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
        </div>
        <div className="space-y-2">
          <label htmlFor="signup-confirm" className="text-sm font-medium">{t('confirmPassword')}</label>
          <Input id="signup-confirm" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="••••••••" />
        </div>
        <Button className="w-full" onClick={handleSignup} disabled={isLoading}>
          {isLoading ? t('submitting') : t('submit')}
        </Button>
      </CardContent>
    </Card>
  );
}


